import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("Verifying your @dataeko.ai account…");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || window.location.hash;

    // Firebase ID token path (from humans.dataeko.ai SSO handoff)
    const firebaseMatch = hash.match(/firebase_token=([^&]+)/);
    if (firebaseMatch) {
      const idToken = decodeURIComponent(firebaseMatch[1]);
      setPhase("Signing you in via Humans of Dataeko…");
      (async () => {
        try {
          const { data } = await api.post("/auth/firebase", { id_token: idToken });
          localStorage.setItem("dataeko_token", data.token);
          setUser(data.user);
          const role = data.user.role;
          const dest = role === "admin" ? "/admin" : role === "accounts" ? "/accounts" : "/timesheet";
          window.history.replaceState({}, "", window.location.pathname);
          nav(dest, { replace: true });
        } catch (e) {
          const d = e.response?.data?.detail;
          setError(typeof d === "string" ? d : "Firebase sign-in failed");
          setTimeout(() => nav("/login", { replace: true }), 2500);
        }
      })();
      return;
    }

    // Emergent-managed Google OAuth path
    const sessionMatch = hash.match(/session_id=([^&]+)/);
    if (sessionMatch) {
      const session_id = decodeURIComponent(sessionMatch[1]);
      (async () => {
        try {
          const { data } = await api.post("/auth/google", { session_id });
          localStorage.setItem("dataeko_token", data.token);
          setUser(data.user);
          const role = data.user.role;
          const dest = role === "admin" ? "/admin" : role === "accounts" ? "/accounts" : "/timesheet";
          window.history.replaceState({}, "", window.location.pathname);
          nav(dest, { replace: true });
        } catch (e) {
          const d = e.response?.data?.detail;
          setError(typeof d === "string" ? d : "Google sign-in failed");
          setTimeout(() => nav("/login", { replace: true }), 2500);
        }
      })();
      return;
    }

    nav("/login", { replace: true });
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
      <div className="text-center">
        {error ? (
          <>
            <div className="text-red-500 dark:text-red-400 text-sm mb-2" data-testid="auth-callback-error">{error}</div>
            <div className="text-xs text-muted-foreground">Redirecting to login…</div>
          </>
        ) : (
          <>
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3 text-blue-600 dark:text-blue-400" />
            <div className="text-sm text-muted-foreground font-mono">{phase}</div>
          </>
        )}
      </div>
    </div>
  );
}
